/*
  window.MMSCart — small shared cart helper (no framework, no global side effects).
  It never dispatches cart events itself; callers decide what to fire.

  MMSCart.getCart({ timeout })                     -> Promise<cart>
  MMSCart.addItems(items, options)                 -> Promise<{ items, data }>
      items    [{ id, quantity, properties?, selling_plan? }] (may be empty when `fields` is given)
      options  {
        fields      URLSearchParams | FormData | null — extra form fields sent first (e.g. a
                    product form's own fields, so the main item rides in the same request)
        attributes  { key: value } — cart attributes
        timeout     ms, default 15000
      }
      Resolves only on HTTP 2xx. `items` is always an array of added line items: the
      response's `items`, or the single line item Shopify returns for a request that
      has a top-level `id`.
      Rejects with an MMSCartError: { type, status, message, description, data }
        type 'cart'    — Shopify refused the add (422 etc.); description is Shopify's text
        type 'http'    — any other non-2xx response
        type 'timeout' — no response within `timeout`
        type 'network' — fetch failed (offline, DNS, CORS, …)
  MMSCart.updateCart(updates, attributes)           -> Promise<cart>
      One /cart/update.js request: line quantities by line key ({ key: qty }) and/or cart
      attributes ({ name: value }, '' removes). Rejects like addItems.
  MMSCart.giftConfig()                              -> { mode: 'choose'|'message'|'off', thresholdCents }
  MMSCart.giftEnforcement(cart, config?)            -> { updates, attributes } | null
      Pure: what the gift rules change in this cart (see "The gift milestone's enforcement").
  MMSCart.isSample(lineItem)                        -> product_type === 'Samples'
  MMSCart.sendAddEvents(lines, options)             -> Promise (resolves when sent or given up)
      Attribution for add-on reporting: after a SUCCESSFUL add, one "add" event per added
      line to the display endpoint, carrying the storefront cart token so the dashboard's
      order import can match order lines to their adds (Order.cartToken). It reads
      /cart.js once for the token (an empty cart's token changes until something is
      added, so never before the add), strips "?key=…", and posts each event with the
      impression sender (keepalive, never in design mode). Fire and forget: it never
      rejects and never touches the cart; flows that reload the page right after the add
      may wait for it (it resolves within about 1.5 s either way).
        lines    [{ surface: 'pdp'|'cart'|'gift', rule_handle, variant_id, product_id?,
                    quantity, source_product_id? (pdp), cart_total_cents? (cart, gift),
                    remaining_cents? (cart) }]
        options  { url?: display endpoint (default EVENT_URL), cart?: a cart object read
                   after the add (skips the read) }
  MMSCart.EVENT_URL                                 -> the display endpoint
  MMSCart.beginBusy() / MMSCart.endBusy()
      Brackets a multi-request cart sequence (add-on add, cart bar add) so the change
      detector and the attribute reconciler never run in the gaps between its requests.
  MMSCart.cartChanged(cart?)
      Tells the change detector the cart may have changed (e.g. a discount code was applied
      or removed). Pass a fresh cart object to skip the /cart.js read.
  MMSCart.refreshCart()                             -> Promise<cart>: reads now (folding in any
      scheduled read); mms:cart-changed has fired by the time it resolves, if the cart changed.
  MMSCart.getLastCart()                             -> the last cart the detector read
  MMSCart.reconcileAttributes(cart) -> { key: value } | null
      Pure: the attribute changes that make `cart.attributes` match `cart.items` ('' removes
      an attribute), or null when nothing needs to change.
  MMSCart.visitorId()                               -> the mms_vid UUID (localStorage)
  MMSCart.sendImpression(url, payload)
      Keepalive text/plain POST of { v: 1, visitor_id, ...payload }. Never in design mode.

  Events (on document), each with detail.cart:
    mms:cart-ready     once, after the first /cart.js read on page load
    mms:cart-changed   once per actual change (see "One cart changed signal" below)

  Attribute reconciler (loaded on every page from layout/theme.liquid):
    Cart attributes outlive the items they describe, so it removes what no longer applies:
      - _addons (the old add-on attribution attribute, no longer written: add events carry
        the attribution now): removed from any cart.
      - cart_upsell_used / cart_upsell_product (legacy, no longer written): both removed when
        no line item's product title matches cart_upsell_product.
      - Empty cart: cart_upsell_used, cart_upsell_product and gift order removed.
      - Any cart: attributes starting with _kite_ (left by the uninstalled Kite app) removed.
    It also applies the gift milestone's enforcement (sample lines and the "Free sample"
    attribute; inert when the gift mode is off) in the same write.
    Runs on every cart read the change detector makes (page load and each change), so it
    makes no reads of its own. It never writes while any cart write is in flight
    (MMSCart's own, a beginBusy() sequence, or any other fetch/XHR POST to
    /cart/add|change|update|clear), nor when a cart write started after that read. It
    writes only when something changes, in one /cart/update.js request, never sends the
    same write for the same cart twice in a row, and dispatches only cart:build (after it
    removed or corrected sample lines, so Impulse redraws them), so it cannot loop.
    Attributes are removed by setting them to '' (Shopify deletes empty-string attributes).

  Gift-order toggle: input[name="attributes[gift order]"] writes the attribute as soon as it
  changes ('Yes' / removed) and is kept in step with the cart on every reconcile read.
*/
(function () {
  'use strict';

  if (window.MMSCart) {
    return;
  }

  var LEGACY_ATTRIBUTION_KEY = '_addons';
  var DEFAULT_TIMEOUT = 15000;
  var EVENT_URL = 'https://mms-shopify-dashboard.vercel.app/api/modules/product-addons/display';

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  function MMSCartError(type, message, extra) {
    var err = new Error(message);
    err.name = 'MMSCartError';
    err.type = type;
    err.status = (extra && extra.status) || 0;
    err.description = (extra && extra.description) || message;
    err.data = (extra && extra.data) || null;
    return err;
  }

  function request(url, init, timeout) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timedOut = false;
    var timer = setTimeout(function () {
      timedOut = true;
      if (controller) controller.abort();
    }, timeout || DEFAULT_TIMEOUT);

    var opts = Object.assign({ credentials: 'same-origin' }, init);
    if (controller) opts.signal = controller.signal;

    return fetch(url, opts)
      .then(
        function (response) {
          return response.text().then(function (text) {
            var data = null;
            try {
              data = text ? JSON.parse(text) : null;
            } catch (e) {
              data = null;
            }
            return { response: response, data: data };
          });
        },
        function (error) {
          if (timedOut) throw MMSCartError('timeout', 'The cart request timed out.');
          throw MMSCartError('network', (error && error.message) || 'Network error');
        }
      )
      .then(function (result) {
        if (timedOut) throw MMSCartError('timeout', 'The cart request timed out.');
        var response = result.response;
        var data = result.data;
        if (!response.ok) {
          var description = (data && (data.description || data.message)) || response.statusText || 'Cart error';
          var type = response.status === 422 || (data && data.status === 422) ? 'cart' : 'http';
          throw MMSCartError(type, (data && data.message) || description, {
            status: response.status,
            description: typeof description === 'string' ? description : JSON.stringify(description),
            data: data
          });
        }
        if (data && typeof data.status === 'number' && data.status >= 400) {
          // Defensive: an error body served with a 2xx status is still an error.
          throw MMSCartError(data.status === 422 ? 'cart' : 'http', data.message || 'Cart error', {
            status: data.status,
            description: data.description || data.message,
            data: data
          });
        }
        return data;
      })
      .finally(function () {
        clearTimeout(timer);
      });
  }

  // ---------- Cart write tracking ----------
  var writesInFlight = 0;  // any cart write, ours or not, currently on the wire
  var busySequences = 0;   // beginBusy() sequences in progress
  var writeGeneration = 0; // bumped whenever a cart write starts
  var reconcileWrite = Promise.resolve(); // settles when the reconciler's write does

  var CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var DISCOUNT_RE = /\/discount\/[^/?#]+/;

  function isCartWrite(url, method) {
    return !!url && String(method || 'GET').toUpperCase() !== 'GET' && CART_WRITE_RE.test(String(url));
  }

  function writeStarted() {
    writesInFlight += 1;
    writeGeneration += 1;
  }

  function writeEnded() {
    writesInFlight = Math.max(0, writesInFlight - 1);
    scheduleDetect();
  }

  function isBusy() {
    return writesInFlight > 0 || busySequences > 0;
  }

  // Passive observers: count cart writes made by any script (Impulse, apps, inline code).
  // They only count; requests and responses pass through untouched.
  if (typeof window.fetch === 'function' && !window.fetch.__mmsCartObserved) {
    var nativeFetch = window.fetch;
    var observedFetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && (input.url || String(input));
      var method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
      if (!isCartWrite(url, method)) {
        var passed = nativeFetch.apply(this, arguments);
        // GET /discount/<code> applies a code (this theme's field, Smile.io and others):
        // not a cart write for the busy guard, but the cart total may have changed.
        if (url && DISCOUNT_RE.test(String(url))) Promise.resolve(passed).then(function () { scheduleDetect(); }, function () {});
        return passed;
      }
      writeStarted();
      var result;
      try {
        result = nativeFetch.apply(this, arguments);
      } catch (e) {
        writeEnded();
        throw e;
      }
      Promise.resolve(result).then(writeEnded, writeEnded);
      return result;
    };
    observedFetch.__mmsCartObserved = true;
    window.fetch = observedFetch;
  }

  if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__mmsCartObserved) {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__mmsCartWrite = isCartWrite(url, method);
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__mmsCartWrite) {
        writeStarted();
        this.addEventListener('loadend', writeEnded, { once: true });
      }
      return xhrSend.apply(this, arguments);
    };
    XMLHttpRequest.prototype.__mmsCartObserved = true;
  }

  function beginBusy() {
    busySequences += 1;
  }

  function endBusy() {
    busySequences = Math.max(0, busySequences - 1);
  }

  // Waits for an in-flight reconciler write, so callers never start from attributes or
  // lines the reconciler is about to replace.
  function getCart(options) {
    var base = routes().cart || '/cart.js';
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now();
    return reconcileWrite.then(function () {
      return request(url, { method: 'GET', headers: { Accept: 'application/json' } }, options && options.timeout);
    });
  }

  function appendItemFields(params, item) {
    params.append('items[][id]', String(item.id));
    params.append('items[][quantity]', String(item.quantity == null ? 1 : item.quantity));
    if (item.selling_plan) params.append('items[][selling_plan]', String(item.selling_plan));
    if (item.properties) {
      Object.keys(item.properties).forEach(function (key) {
        params.append('items[][properties][' + key + ']', String(item.properties[key]));
      });
    }
  }

  function addItems(items, options) {
    options = options || {};
    items = items || [];
    var params = new URLSearchParams();

    if (options.fields) {
      var entries = typeof options.fields.entries === 'function' ? options.fields.entries() : [];
      var next;
      while (!(next = entries.next()).done) {
        // Skip File values; the product form never posts files to /cart/add.js.
        if (typeof next.value[1] === 'string') params.append(next.value[0], next.value[1]);
      }
    }

    items.forEach(function (item) { appendItemFields(params, item); });

    if (options.attributes) {
      Object.keys(options.attributes).forEach(function (key) {
        params.append('attributes[' + key + ']', String(options.attributes[key]));
      });
    }

    if (!items.length && !options.fields) {
      return Promise.reject(MMSCartError('cart', 'Nothing to add.'));
    }

    return request(routes().cartAdd || '/cart/add.js', {
      method: 'POST',
      body: params.toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      }
    }, options.timeout).then(function (data) {
      var added = data && Array.isArray(data.items) ? data.items : data ? [data] : [];
      return { items: added, data: data };
    });
  }

  // ---------- Add events (add-on reporting attribution) ----------
  // The storefront cart token as the order import stores it (Order.cartToken): /cart.js
  // "token" without "?key=…".
  function cleanCartToken(token) {
    if (typeof token !== 'string' || !token) return null;
    return token.split('?')[0] || null;
  }

  function sendAddEvents(lines, options) {
    options = options || {};
    var url = options.url || EVENT_URL;
    lines = (lines || []).filter(function (line) {
      return line && line.variant_id > 0 && line.quantity > 0 && line.surface && line.rule_handle;
    });
    if (!lines.length || (window.Shopify && window.Shopify.designMode)) return Promise.resolve();
    var cartPromise = options.cart && options.cart.token ? Promise.resolve(options.cart) : request(
      (routes().cart || '/cart.js') + '?t=' + Date.now(),
      { method: 'GET', headers: { Accept: 'application/json' } },
      1500
    );
    var sent = cartPromise
      .then(function (cart) { return cleanCartToken(cart && cart.token); }, function () { return null; })
      .then(function (token) {
        lines.forEach(function (line) {
          var payload = { event: 'add' };
          Object.keys(line).forEach(function (key) {
            if (line[key] !== undefined && line[key] !== null) payload[key] = line[key];
          });
          if (token) payload.cart_token = token;
          sendImpression(url, payload);
        });
      })
      .catch(function () { /* never affects the cart */ });
    // Callers that reload the page right after an add wait at most this long.
    return Promise.race([sent, new Promise(function (resolve) { setTimeout(resolve, 1600); })]);
  }

  // ---------- Attribute reconciler ----------
  var UPSELL_USED_KEY = 'cart_upsell_used';
  var UPSELL_PRODUCT_KEY = 'cart_upsell_product';
  var GIFT_KEY = 'gift order';
  var EMPTY_CART_KEYS = [UPSELL_USED_KEY, UPSELL_PRODUCT_KEY, GIFT_KEY];
  var KITE_PREFIX = '_kite_';
  var RECONCILE_DELAY = 400;

  function hasValue(attrs, key) {
    return attrs[key] !== undefined && attrs[key] !== null && attrs[key] !== '';
  }

  function reconcileAttributes(cart) {
    if (!cart || typeof cart !== 'object') return null;
    var attrs = cart.attributes || {};
    var items = Array.isArray(cart.items) ? cart.items : [];
    var changes = {};

    // The old attribution attribute is no longer written (add events replaced it): any cart
    // still carrying one loses it.
    if (hasValue(attrs, LEGACY_ATTRIBUTION_KEY)) changes[LEGACY_ATTRIBUTION_KEY] = '';

    if (!items.length) {
      EMPTY_CART_KEYS.forEach(function (key) {
        if (hasValue(attrs, key)) changes[key] = '';
      });
    } else {
      if (hasValue(attrs, UPSELL_USED_KEY) || hasValue(attrs, UPSELL_PRODUCT_KEY)) {
        var upsellTitle = String(attrs[UPSELL_PRODUCT_KEY] || '');
        var stillThere = upsellTitle !== '' && items.some(function (item) {
          return item.product_title === upsellTitle;
        });
        if (!stillThere) {
          if (hasValue(attrs, UPSELL_USED_KEY)) changes[UPSELL_USED_KEY] = '';
          if (hasValue(attrs, UPSELL_PRODUCT_KEY)) changes[UPSELL_PRODUCT_KEY] = '';
        }
      }
    }

    // Leftovers of the uninstalled Kite free-gift app (_kite_ccdData, _kite_cfAdditionalData, …)
    // on carts from before it was removed: nothing reads them, so they go in this same write.
    Object.keys(attrs).forEach(function (key) {
      if (key.indexOf(KITE_PREFIX) === 0 && hasValue(attrs, key)) changes[key] = '';
    });

    return Object.keys(changes).length ? changes : null;
  }

  function giftToggles() {
    return document.querySelectorAll('input[type="checkbox"][name="attributes[gift order]"]');
  }

  function syncGiftToggles(attrs) {
    var on = hasValue(attrs || {}, GIFT_KEY);
    Array.prototype.forEach.call(giftToggles(), function (box) {
      if (!box.__mmsGiftWriting) box.checked = on;
    });
  }

  function updateAttributes(changes) {
    return updateCart(null, changes);
  }

  // One /cart/update.js request: line quantities by line key ({ key: qty }) and/or
  // attributes ({ name: value }, '' removes). Resolves with the cart.
  function updateCart(updates, attributes) {
    var body = {};
    if (updates && Object.keys(updates).length) body.updates = updates;
    if (attributes && Object.keys(attributes).length) body.attributes = attributes;
    return request(routes().cartUpdate || '/cart/update.js', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    });
  }

  // ---------- The gift milestone's enforcement ----------
  // mms-shopify-dashboard/docs/cart-milestones/spec.md, "Gift milestone" > "Enforcement"
  // (mirrored by lib/cart-milestones/gift.ts resolveGift). A sample is a line whose
  // product_type is exactly "Samples", however it got into the cart (the picker, another
  // app, a direct link, an old cart). The mode and threshold come from #mms-gift-config
  // (layout/theme.liquid, the same values the gift panel renders from).
  //   0. off (or no config): inert. No line or attribute is touched.
  //   1. message mode, or choose mode below the threshold: every sample line is removed.
  //   2. choose mode at or above: ONE sample line is kept (the first marked _mms_gift "1",
  //      else the first in cart order); every other sample line is removed.
  //   3. The kept line's quantity is set to 1 if it is higher.
  //   The visible attribute "Free sample: include": set in message mode at or above the
  //   threshold, removed otherwise in choose and message mode, written only when it differs.
  // A removed sample is never re-added. It runs inside the reconciler below (same read,
  // same busy guard, one /cart/update.js write), so a cart change gets one enforcement.
  var SAMPLE_TYPE = 'Samples';
  var GIFT_PROPERTY = '_mms_gift';
  var GIFT_ATTRIBUTE = 'Free sample';
  var GIFT_ATTRIBUTE_VALUE = 'include';
  var giftConfigValue = null;

  function giftConfig() {
    if (giftConfigValue) return giftConfigValue;
    var el = document.getElementById('mms-gift-config');
    if (!el) return { mode: 'off', thresholdCents: 0 }; // not parsed yet: inert, retry next read
    var cfg = null;
    try {
      cfg = JSON.parse(el.textContent);
    } catch (e) {
      cfg = null;
    }
    var mode = cfg && (cfg.mode === 'choose' || cfg.mode === 'message') ? cfg.mode : 'off';
    var cents = cfg && Number(cfg.thresholdCents) > 0 ? Math.round(Number(cfg.thresholdCents)) : 2500;
    giftConfigValue = { mode: mode, thresholdCents: cents };
    return giftConfigValue;
  }

  function isSample(item) {
    return !!item && item.product_type === SAMPLE_TYPE;
  }

  function isGiftMarked(item) {
    return !!(item && item.properties && String(item.properties[GIFT_PROPERTY]) === '1');
  }

  // Pure: { updates: { lineKey: qty }, attributes: { name: value } } that bring the cart
  // in line with the gift rules, or null when it already is (always null when off).
  function giftEnforcement(cart, config) {
    config = config || giftConfig();
    if (!cart || config.mode === 'off') return null;
    var items = Array.isArray(cart.items) ? cart.items : [];
    var attrs = cart.attributes || {};
    var qualifies = Number(cart.total_price) >= config.thresholdCents;
    var samples = items.filter(isSample);
    var updates = {};
    var attributes = {};

    if (config.mode === 'message' || !qualifies) {
      samples.forEach(function (item) { updates[item.key] = 0; });
    } else {
      var kept = samples.filter(isGiftMarked)[0] || samples[0] || null;
      samples.forEach(function (item) {
        if (item === kept) {
          if (item.quantity > 1) updates[item.key] = 1;
        } else {
          updates[item.key] = 0;
        }
      });
    }

    var want = config.mode === 'message' && qualifies;
    var has = attrs[GIFT_ATTRIBUTE];
    if (want && has !== GIFT_ATTRIBUTE_VALUE) attributes[GIFT_ATTRIBUTE] = GIFT_ATTRIBUTE_VALUE;
    if (!want && hasValue(attrs, GIFT_ATTRIBUTE)) attributes[GIFT_ATTRIBUTE] = '';

    if (!Object.keys(updates).length && !Object.keys(attributes).length) return null;
    return { updates: updates, attributes: attributes };
  }

  // The last write attempted for a cart state. The same plan for the same cart is not
  // sent twice in a row (a refused write must not loop); a read that needs no write clears it.
  var lastAttempt = null;

  // Runs on every cart read the change detector makes (no read of its own). Writes only
  // when something changes, and never when a cart write started after that read.
  function reconcile(cart, generation) {
    syncGiftToggles(cart && cart.attributes);
    var changes = reconcileAttributes(cart);
    var gift = giftEnforcement(cart);
    if (!changes && !gift) {
      lastAttempt = null;
      return;
    }
    if (isBusy() || generation !== writeGeneration) {
      // The read may be stale; the write that's running triggers another read.
      scheduleDetect();
      return;
    }
    var attributes = Object.assign({}, changes || {}, gift ? gift.attributes : {});
    var updates = gift ? gift.updates : {};
    var attempt = cartSignature(cart) + '#' + JSON.stringify([updates, attributes]);
    if (attempt === lastAttempt) return;
    lastAttempt = attempt;
    var linesChange = Object.keys(updates).length > 0;
    var write = updateCart(updates, attributes);
    reconcileWrite = write.then(function () {}, function () {});
    write.then(function (updated) {
      syncGiftToggles(updated && updated.attributes);
      // Sample lines removed or corrected: Impulse redraws its line items (drawer and
      // /cart). The write itself already triggers the detector's read, which fires
      // mms:cart-changed once. An emptied /cart page reloads into its empty state.
      if (!linesChange) return;
      if (document.getElementById('CartPageForm') && updated && updated.item_count === 0) {
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:build'));
    }, function () {});
  }

  // ---------- One "cart changed" signal ----------
  // Every source of cart changes funnels here: observed cart writes (ours, Impulse's
  // quantity changes, other apps), Impulse's cart:updated / cart:build,
  // ajaxProduct:added, and MMSCart.cartChanged() (discount codes). After a short quiet
  // period, and never while a cart write is in flight, it reads /cart.js once and fires
  // mms:cart-changed { cart } only if the cart's lines, quantities, line totals, total or
  // discounts differ from the last read. Attribute-only writes therefore never fire it.
  // The first read (page load) fires mms:cart-ready { cart } instead.
  var DETECT_DELAY = RECONCILE_DELAY > 150 ? 150 : RECONCILE_DELAY;
  var detectTimer = null;
  var detecting = false;
  var detectAgain = false;
  var lastSignature = null;
  var lastCart = null;
  var cartReady = false;

  function cartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    var lines = cart.items.map(function (i) { return i.key + ':' + i.quantity + ':' + i.final_line_price; }).join(',');
    var codes = (cart.cart_level_discount_applications || []).map(function (d) {
      return d.title + ':' + d.total_allocated_amount;
    }).join(',');
    return [cart.item_count, cart.total_price, cart.total_discount, lines, codes].join('|');
  }

  function emit(name, cart) {
    document.dispatchEvent(new CustomEvent(name, { detail: { cart: cart } }));
  }

  function handleCart(cart, generation) {
    if (!cart || !Array.isArray(cart.items)) return;
    lastCart = cart;
    var sig = cartSignature(cart);
    if (!cartReady) {
      cartReady = true;
      lastSignature = sig;
      emit('mms:cart-ready', cart);
    } else if (sig !== lastSignature) {
      lastSignature = sig;
      emit('mms:cart-changed', cart);
    }
    reconcile(cart, generation);
  }

  function scheduleDetect(delay) {
    clearTimeout(detectTimer);
    detectTimer = setTimeout(runDetect, delay == null ? DETECT_DELAY : delay);
  }

  var detectPromise = Promise.resolve(null);

  function runDetect() {
    if (detecting) {
      detectAgain = true;
      return detectPromise;
    }
    if (isBusy()) {
      scheduleDetect();
      return detectPromise;
    }
    detecting = true;
    var generation = writeGeneration;
    var base = routes().cart || '/cart.js';
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now();
    detectPromise = request(url, { method: 'GET', headers: { Accept: 'application/json' } })
      .then(function (cart) {
        handleCart(cart, generation);
        return cart;
      })
      .catch(function () { return null; /* best effort; the next change or page load retries */ })
      .then(function (cart) {
        detecting = false;
        if (detectAgain) {
          detectAgain = false;
          scheduleDetect();
        }
        return cart;
      });
    return detectPromise;
  }

  // Reads the cart now (folding in any scheduled read) and resolves with it, after
  // mms:cart-changed has fired if it changed. For callers that need the cart themselves.
  function refreshCart() {
    clearTimeout(detectTimer);
    if (detecting) return detectPromise.then(function () { return runDetect(); });
    return runDetect();
  }

  // Tells the detector the cart may have changed. With a fresh cart object (e.g. a
  // /cart/update.js response, or a cart the caller just read) it skips the read.
  function cartChanged(cart) {
    if (cart && Array.isArray(cart.items) && !isBusy() && !detecting) {
      clearTimeout(detectTimer);
      handleCart(cart, writeGeneration);
      return;
    }
    scheduleDetect();
  }

  document.addEventListener('cart:updated', function (evt) {
    // Impulse passes the cart it just rebuilt from; reuse it rather than reading again.
    cartChanged(evt && evt.detail && evt.detail.cart);
  });
  ['ajaxProduct:added', 'cart:build'].forEach(function (name) {
    document.addEventListener(name, function () { scheduleDetect(); });
  });

  // Gift-order toggle: write the attribute as soon as it changes.
  document.addEventListener('change', function (evt) {
    var box = evt.target;
    if (!box || box.type !== 'checkbox' || box.name !== 'attributes[' + GIFT_KEY + ']') return;
    var on = box.checked;
    Array.prototype.forEach.call(giftToggles(), function (other) { other.checked = on; });
    box.__mmsGiftWriting = true;
    var changes = {};
    changes[GIFT_KEY] = on ? (box.value || 'Yes') : '';
    updateAttributes(changes)
      .catch(function () {
        Array.prototype.forEach.call(giftToggles(), function (other) { other.checked = !on; });
      })
      .then(function () { box.__mmsGiftWriting = false; });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleDetect(0); });
  } else {
    scheduleDetect(0);
  }

  // ---------- Impressions (shared by the add-on block and the cart milestone bar) ----------
  var visitorIdValue = null;

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var b = new Uint8Array(16);
    window.crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return (x + 0x100).toString(16).slice(1); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  // Stored in localStorage under mms_vid; a per-page-load UUID if storage is unavailable.
  function visitorId() {
    if (visitorIdValue) return visitorIdValue;
    var id = null;
    try {
      id = window.localStorage.getItem('mms_vid');
      if (!id || !/^[0-9a-f-]{36}$/i.test(id)) {
        id = uuid();
        window.localStorage.setItem('mms_vid', id);
      }
    } catch (e) {
      id = null;
    }
    visitorIdValue = id || uuid();
    return visitorIdValue;
  }

  // POSTs one impression; adds v: 1 and visitor_id. Plain fetch, not sendBeacon: Chrome
  // types beacons as "ping" and EasyPrivacy blocks third-party pings. text/plain keeps it
  // a CORS simple request (no preflight); keepalive lets it finish if the page unloads.
  // Never in the theme editor. Errors are swallowed: impressions never affect the page.
  function sendImpression(url, payload) {
    if (!url || (window.Shopify && window.Shopify.designMode)) return;
    try {
      var body = Object.assign({ v: 1, visitor_id: visitorId() }, payload);
      fetch(url, {
        method: 'POST',
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(body)
      }).catch(function () {});
    } catch (e) {
      // ignore
    }
  }

  window.MMSCart = Object.freeze({
    EVENT_URL: EVENT_URL,
    getCart: getCart,
    getLastCart: function () { return lastCart; },
    addItems: addItems,
    updateCart: updateCart,
    giftConfig: giftConfig,
    giftEnforcement: giftEnforcement,
    isSample: isSample,
    sendAddEvents: sendAddEvents,
    beginBusy: beginBusy,
    endBusy: endBusy,
    cartChanged: cartChanged,
    refreshCart: refreshCart,
    reconcileAttributes: reconcileAttributes,
    visitorId: visitorId,
    sendImpression: sendImpression
  });
})();

// Synced with the add-event attribution change (fa9b838).
